'use client';

import { KeyCodes } from '@coolblue-development/constants';
import { Link, type LinkProps } from '@coolblue-development/freddy';
import { ResolvedGA4Trigger } from '@coolblue-development/ga4-events';
import { useAnalytics } from '@coolblue-development/next-analytics/client';
import { PropertyExplanationModal } from '@coolblue-development/next-hemingway/client';
import { type KeyboardEvent, type ReactNode, useState } from 'react';

export type PropertyExplanationTriggerProps = {
  children: ReactNode;
  productTypeId: number;
  propertyId: number;
  displayName: string;
  trackingOpenTrigger?: ResolvedGA4Trigger;
  trackingCloseTrigger?: ResolvedGA4Trigger;
  component?: 'button' | 'span';
} & LinkProps;

export default function PropertyExplanationTrigger({
  children,
  productTypeId,
  propertyId,
  displayName,
  trackingOpenTrigger,
  trackingCloseTrigger,
  component = 'button',
  ...rest
}: PropertyExplanationTriggerProps) {
  const [showPropertyExplanation, setShowPropertyExplanation] = useState(false);
  const { trackEvent } = useAnalytics();

  function handlePropertyExplanationOpen() {
    setShowPropertyExplanation(true);
    if (trackingOpenTrigger) {
      trackEvent(trackingOpenTrigger);
    }
  }

  function handlePropertyExplanationClose() {
    setShowPropertyExplanation(false);
    if (trackingCloseTrigger) {
      trackEvent(trackingCloseTrigger);
    }
  }

  const a11yProps =
    component !== 'button'
      ? {
          role: 'button',
          tabIndex: 0,
          onKeyDown: (event: KeyboardEvent) => {
            const { code } = event;
            if (code === KeyCodes.ENTER || code === KeyCodes.SPACE) {
              event.preventDefault();
            }
          }
        }
      : {};

  return (
    <>
      <Link component={component} onClick={handlePropertyExplanationOpen} {...a11yProps} {...rest}>
        {children}
      </Link>
      {showPropertyExplanation && (
        <PropertyExplanationModal
          productTypeId={String(productTypeId)}
          property={{
            displayName,
            hasExplanation: true,
            propertyId: String(propertyId)
          }}
          open={showPropertyExplanation}
          onClose={handlePropertyExplanationClose}
        />
      )}
    </>
  );
}
